import {
  Alert,
  Button,
  createCssVariableString,
  MatchUp,
  Modal,
  Typography,
  useLoading,
  useIsVisible,
  Section,
  useBreakpoints,
} from "best-common-react";
import React, { memo, RefObject, useRef, useState, lazy, Suspense, useMemo } from "react";
import { bulkRefundGame } from "../../../api/RequesTixApi";
import { GamePreviewDTO } from "../../../types/Game";
import { TicketRequestDTO } from "../../../types/TicketRequest";
import { useTimezone } from "../../../util/TimeUtil";
import EventCanceled from "../misc/EventCanceled";
import GamePreviewDisplay from "../../game/GamePreviewDisplay";
import { isAdminGameCollapsed, setAdminGameExpanded } from "../../../util/TicketRequestUtil";
const AdminRequestTable = lazy(() => import("./AdminRequestTable"));

type RefundGameModalProps = {
  show: boolean;
  game: GamePreviewDTO;
  onClose: () => void;
};

export const RefundGameModal = ({ show, game, onClose }: RefundGameModalProps) => {
  const { setLoading } = useLoading();

  const submitted = useRef(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>();

  const refundGame = async () => {
    if (!game.gamePk || submitted.current) return;
    submitted.current = true;
    setProcessing(true);
    setLoading(true);
    try {
      await bulkRefundGame(game.gamePk);
      onClose();
    } catch {
      setError(
        "One or more refund outcomes are uncertain. Check payment history before retrying; some refunds may have completed.",
      );
    } finally {
      setProcessing(false);
      setLoading(false);
    }
  };

  return !!game ? (
    <Modal show={show} size="lg">
      <Modal.Header close={processing ? undefined : onClose}>Are you sure?</Modal.Header>
      <Modal.Body>
        {error && (
          <Alert type="danger">
            <Typography>{error}</Typography>
          </Alert>
        )}
        <GamePreviewDisplay game={game} />
        <div className="bcr-mt-3">
          <b>Are you sure you want to bulk refund this game?</b>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-end">
          <Button
            variant="primary"
            className="bcr-me-2"
            disabled={!game.gamePk || processing || submitted.current}
            onClick={() => refundGame()}
          >
            Refund Game
          </Button>
          <Button variant="default" disabled={processing} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  ) : null;
};

type AdminRequestsGameProps = {
  game: GamePreviewDTO;
  requests: TicketRequestDTO[];
  jumpToRequestId?: string | number;
};

const AdminRequestsGame = memo(({ game, requests = [], jumpToRequestId }: AdminRequestsGameProps) => {
  const [showRefund, setShowRefund] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const isVisible = useIsVisible(ref as RefObject<HTMLElement>, true, 0);
  const tz = useMemo(() => (game?.venue ? game.venue.timeZone : null), [game]);
  const { time, day, date } = useTimezone(game?.gameDate, tz!, game?.status);
  const gamePk = useMemo(() => game?.gamePk, [game]);
  const { isMobile } = useBreakpoints();
  const isExpanded = useMemo(() => {
    const isCollapsed = isAdminGameCollapsed(gamePk);
    if (isCollapsed) {
      return false;
    } else {
      return requests.length > 0 && !isMobile;
    }
  }, [requests, gamePk, isMobile]);

  const onToggle = (open: boolean) => {
    setAdminGameExpanded(gamePk, open);
  };

  if (!game) return null;

  return (
    <div ref={ref}>
      <Section
        header={
          <div className="d-flex align-items-center w-100 bcr-gap-2">
            <div className="d-flex flex-column bcr-gap-1">
              <div className="d-flex align-items-center flex-wrap bcr-gap-2">
                <Typography variant="titleMedium" elementStyles={{ weight: "bold" }}>
                  {date}
                </Typography>
                <MatchUp home={game.teams.home.team} away={game.teams.away.team} description={game.seriesDescription} />
              </div>
              <div className="d-flex align-items-center flex-wrap bcr-gap-2">
                <Typography
                  elementStyles={{
                    color: createCssVariableString("onColorsOnSurfaceLow"),
                  }}
                >
                  {day} {time}
                </Typography>
                <EventCanceled game={game} />
              </div>
            </div>
            <div className="bcr-ms-auto ">
              <Typography
                variant="bodySmall"
                style={{
                  fontStyle: "italic",
                  color: createCssVariableString("color-on-surface-low"),
                }}
              >
                {requests.length} {requests.length === 1 ? "Request" : "Requests"}
              </Typography>
            </div>
          </div>
        }
        sectionKey={`game-${gamePk}`}
        startsOpen={isExpanded}
        onToggle={onToggle}
      >
        <div>
          <Button variant="default" onClick={() => setShowRefund(true)}>
            Refund Game
          </Button>
          {showRefund && <RefundGameModal game={game} show={showRefund} onClose={() => setShowRefund(false)} />}
        </div>
        {!!(requests.length > 0) && (
          <div className="bcr-mt-2">
            <Suspense fallback={null}>
              {isVisible && (
                <AdminRequestTable requests={requests} jumpToRequestId={jumpToRequestId} visible={isVisible} />
              )}
            </Suspense>
          </div>
        )}
      </Section>
    </div>
  );
});

export default AdminRequestsGame;
